import { Controller } from "@hotwired/stimulus"

// Visibility-aware mark-as-read for an open conversation page.
//
// When a new message arrives via Turbo Stream and the user is actively
// viewing the page (tab visible AND window focused), POST to the mark-read
// endpoint after a short debounce. Also fires when the user returns to a
// previously backgrounded tab while still on the conversation page.
//
// HIPAA framing: the visibility gate is what makes read_at on each Message
// a trustworthy access timestamp. Without it we'd record reads for
// messages that arrived while the user was on another tab or had the
// window in the background — falsifying the per-message audit trail.
//
// The server endpoint is idempotent (MarkReadService only updates rows
// where read_at IS NULL), so duplicate POSTs from bursts, retries, or
// multiple open tabs are safe.
export default class extends Controller {
  static values = {
    url: String,
    delayMs: { type: Number, default: 1500 },
    targetId: { type: String, default: "messages" }
  }

  connect() {
    this.timer = null
    this.streamHandler = this.handleStreamRender.bind(this)
    this.visibilityHandler = this.handleVisibilityChange.bind(this)
    this.focusHandler = this.handleFocus.bind(this)

    document.addEventListener("turbo:before-stream-render", this.streamHandler)
    document.addEventListener("visibilitychange", this.visibilityHandler)
    window.addEventListener("focus", this.focusHandler)
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.streamHandler)
    document.removeEventListener("visibilitychange", this.visibilityHandler)
    window.removeEventListener("focus", this.focusHandler)
    this.cancelPending()
  }

  handleStreamRender(event) {
    const stream = event.detail?.newStream
    if (!stream) return
    if (stream.getAttribute("target") !== this.targetIdValue) return
    if (stream.getAttribute("action") !== "append") return

    this.scheduleMarkRead()
  }

  handleVisibilityChange() {
    if (this.isActivelyViewing()) {
      this.scheduleMarkRead()
    }
  }

  handleFocus() {
    if (this.isActivelyViewing()) {
      this.scheduleMarkRead()
    }
  }

  scheduleMarkRead() {
    if (!this.isActivelyViewing()) return
    this.cancelPending()
    this.timer = setTimeout(() => this.fireMarkRead(), this.delayMsValue)
  }

  cancelPending() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  isActivelyViewing() {
    return document.visibilityState === "visible" && document.hasFocus()
  }

  async fireMarkRead() {
    this.timer = null
    // Re-check at fire time — the user may have switched away during the
    // debounce window.
    if (!this.isActivelyViewing()) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken || "",
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin"
      })
    } catch (err) {
      // Network blip — the next page-mount will run MarkReadService server-side.
      console.warn("[messages-reader] mark-read failed:", err)
    }
  }
}
